'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/* =========================================================
   FORUM BOOT SPLASH — MASTER SETTINGS
   1 = enabled
   0 = disabled
========================================================= */
const FORUM_SPLASH_ENABLED = 1

/* =========================================================
   TIMING
========================================================= */
const FORUM_SPLASH_SHOW_MS = 10000           // legacy fallback mode only
const FORUM_SPLASH_CLOSE_ON_VIDEO_END = 1    // 1 = close when video ends, 0 = ignore video end
const FORUM_SPLASH_FAILSAFE_MS = 45000       // emergency close only if video never ends/errors
const FORUM_SPLASH_FADE_OUT_MS = 320         // fade-out duration

/* =========================================================
   VIDEO SOURCE
   File must exist in: public/load/load.mp4
   URL for Next/public usage: /load/load.mp4
========================================================= */
const FORUM_SPLASH_VIDEO_SRC = '/load/load.mp4'

/* =========================================================
   SOUND SETTINGS
========================================================= */
// 1 = try to start with sound immediately
// 0 = always start muted
const FORUM_SPLASH_TRY_SOUND_AUTOPLAY = 1

// If sound autoplay is blocked by browser:
// 1 = fallback to muted autoplay
// 0 = do not fallback
const FORUM_SPLASH_ALLOW_MUTED_FALLBACK = 1

// Initial volume if sound autoplay succeeds
const FORUM_SPLASH_VOLUME = 1

/* =========================================================
   LAYER / Z-INDEX
========================================================= */
const FORUM_SPLASH_Z_INDEX = 2147483647

/* =========================================================
   OVERLAY POSITIONING
========================================================= */
const FORUM_SPLASH_OVERLAY_INSET = '0px'
const FORUM_SPLASH_ALIGN_X = 'center'     // center | flex-start | flex-end
const FORUM_SPLASH_ALIGN_Y = 'center'     // center | flex-start | flex-end
const FORUM_SPLASH_PADDING = '0px'

/* =========================================================
   OVERLAY BACKGROUND
========================================================= */
const FORUM_SPLASH_BACKDROP = 'rgba(0,0,0,0.94)'

/* =========================================================
   VIDEO FRAME GEOMETRY (9:16 PRESERVED)
========================================================= */
const FORUM_SPLASH_ASPECT_RATIO = '9 / 16'

// 'vw' | 'vh' | 'px'
const FORUM_SPLASH_SIZE_MODE = 'vh'

const FORUM_SPLASH_WIDTH_VW = 56
const FORUM_SPLASH_WIDTH_VH = 100
const FORUM_SPLASH_WIDTH_PX = 720

const FORUM_SPLASH_MIN_WIDTH = '240px'
const FORUM_SPLASH_MAX_WIDTH = '100vw'
const FORUM_SPLASH_MAX_HEIGHT = '100vh'

const FORUM_SPLASH_OFFSET_X = '0px'
const FORUM_SPLASH_OFFSET_Y = '0px'

const FORUM_SPLASH_BORDER_RADIUS = '0px'
const FORUM_SPLASH_BORDER = '0px solid transparent'
const FORUM_SPLASH_BOX_SHADOW = 'none'
const FORUM_SPLASH_OVERFLOW = 'hidden'

/* =========================================================
   VIDEO FIT / LOOK
========================================================= */
const FORUM_SPLASH_OBJECT_FIT = 'contain' // contain | cover | fill
const FORUM_SPLASH_OBJECT_POSITION = 'center center'
const FORUM_SPLASH_VIDEO_BACKGROUND = '#00000000'
const FORUM_SPLASH_VIDEO_OPACITY = 1

/* =========================================================
   OPTIONAL DEBUG
========================================================= */
const FORUM_SPLASH_DEBUG = 0

function getFrameWidth() {
  if (FORUM_SPLASH_SIZE_MODE === 'px') return `${FORUM_SPLASH_WIDTH_PX}px`
  if (FORUM_SPLASH_SIZE_MODE === 'vh') return `${FORUM_SPLASH_WIDTH_VH}vh`
  return `${FORUM_SPLASH_WIDTH_VW}vw`
}

function shouldShowSplashNow() {
  if (FORUM_SPLASH_ENABLED !== 1) return false
  if (typeof window === 'undefined') return true
  return window.__QL7_FORUM_SPLASH_DONE__ !== '1'
}

function attachSplashSource(el) {
  if (!el) return false
  const expectedSrc = FORUM_SPLASH_VIDEO_SRC
  try {
    if (el.dataset.ql7SplashSrcAttached === '1' && el.getAttribute('src') === expectedSrc) {
      return false
    }
    el.dataset.ql7SplashSrcAttached = '1'
    el.setAttribute('src', expectedSrc)
    return true
  } catch {
    return false
  }
}

function isIOSWebKitSplashRuntime() {
  if (typeof navigator === 'undefined') return false
  try {
    const ua = String(navigator.userAgent || '')
    const classicIOS = /iP(hone|ad|od)/i.test(ua)
    const desktopModeIPad =
      /Macintosh/i.test(ua) && Number(navigator.maxTouchPoints || 0) > 1
    return !!((classicIOS || desktopModeIPad) && /AppleWebKit/i.test(ua))
  } catch {
    return false
  }
}

function BootSplashEnergyOverlay() {
  return (
    <svg
      className="forum-boot-splash__energy-svg"
      viewBox="0 0 1600 900"
      preserveAspectRatio="none"
      aria-hidden="true"
      style={{ position:'absolute', inset:0, width:'100%', height:'100%', display:'block', opacity:.72 }}
    >
      <defs>
        <linearGradient id="ql7PageBolt" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#65dcff" stopOpacity=".03" />
          <stop offset="26%" stopColor="#8beaff" stopOpacity=".48" />
          <stop offset="54%" stopColor="#55a7ff" stopOpacity=".70" />
          <stop offset="80%" stopColor="#7deeff" stopOpacity=".46" />
          <stop offset="100%" stopColor="#69d7ff" stopOpacity=".03" />
        </linearGradient>
      </defs>

      <g fill="none" strokeLinecap="round" strokeLinejoin="round" opacity=".56">
        <path d="M14 132 148 214 104 282 278 338 213 414 346 468 279 538 430 612" stroke="url(#ql7PageBolt)" strokeWidth="2" />
        <path d="M0 724 124 652 86 584 236 532 180 458 322 392 270 320 422 238" stroke="url(#ql7PageBolt)" strokeWidth="1.4" />
        <path d="M58 842 142 758 116 704 224 638 198 584 296 520" stroke="#78e8ff" strokeOpacity=".38" strokeWidth=".9" />
        <path d="M1586 132 1452 214 1496 282 1322 338 1387 414 1254 468 1321 538 1170 612" stroke="url(#ql7PageBolt)" strokeWidth="2" />
        <path d="M1600 724 1476 652 1514 584 1364 532 1420 458 1278 392 1330 320 1178 238" stroke="url(#ql7PageBolt)" strokeWidth="1.4" />
        <path d="M1542 842 1458 758 1484 704 1376 638 1402 584 1304 520" stroke="#78e8ff" strokeOpacity=".38" strokeWidth=".9" />
        <path d="M64 410l52-28m-20 70 72 12m82 192 46-42m56-422 32 46M1536 410l-52-28m20 70-72 12m-82 192-46-42m-56-422-32 46" stroke="#87efff" strokeOpacity=".34" strokeWidth=".7" />
      </g>

      <g
        fill="none"
        stroke="#c8f7ff"
        strokeWidth="1"
        strokeOpacity=".66"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray="78 520"
        strokeDashoffset="0"
      >
        <animate attributeName="stroke-dashoffset" from="0" to="-598" dur="5.6s" repeatCount="indefinite" />
        <path d="M14 132 148 214 104 282 278 338 213 414 346 468 279 538 430 612" />
        <path d="M0 724 124 652 86 584 236 532 180 458 322 392 270 320 422 238" />
        <path d="M1586 132 1452 214 1496 282 1322 338 1387 414 1254 468 1321 538 1170 612" />
        <path d="M1600 724 1476 652 1514 584 1364 532 1420 458 1278 392 1330 320 1178 238" />
      </g>
    </svg>
  )
}

export default function ForumBootSplash({ onDone }) {
  const [visible, setVisible] = useState(() => shouldShowSplashNow())
  const [closing, setClosing] = useState(false)
  const [playbackReady, setPlaybackReady] = useState(false)
  const [soundAutoplayBlocked, setSoundAutoplayBlocked] = useState(false)
const closeTimerRef = useRef(0)
const fadeTimerRef = useRef(0)
const videoRef = useRef(null)
const startOnceRef = useRef(false)
const finishedRef = useRef(false)
const userSoundGestureRef = useRef(false)

  const frameWidth = useMemo(() => getFrameWidth(), [])

const splashStyle = useMemo(() => ({
  position: 'fixed',
  inset: FORUM_SPLASH_OVERLAY_INSET,
  zIndex: FORUM_SPLASH_Z_INDEX,
  display: 'flex',
  alignItems: FORUM_SPLASH_ALIGN_Y,
  justifyContent: FORUM_SPLASH_ALIGN_X,
  padding: FORUM_SPLASH_PADDING,
  background: FORUM_SPLASH_BACKDROP,
  backdropFilter: 'none',
  WebkitBackdropFilter: 'none',

  /*
   * Splash itself receives pointer events,
   * but browser must not interpret touch gestures as scrolling.
   */
  pointerEvents: 'auto',
  touchAction: 'none',
  overscrollBehavior: 'none',

  opacity: closing ? 0 : 1,
  transition: `opacity ${FORUM_SPLASH_FADE_OUT_MS}ms ease`,
}), [closing])

  const frameStyle = useMemo(() => ({
    position: 'relative',
    width: frameWidth,
    minWidth: FORUM_SPLASH_MIN_WIDTH,
    maxWidth: FORUM_SPLASH_MAX_WIDTH,
    maxHeight: FORUM_SPLASH_MAX_HEIGHT,
    aspectRatio: FORUM_SPLASH_ASPECT_RATIO,
    left: FORUM_SPLASH_OFFSET_X,
    top: FORUM_SPLASH_OFFSET_Y,
    borderRadius: FORUM_SPLASH_BORDER_RADIUS,
    border: FORUM_SPLASH_BORDER,
    boxShadow: FORUM_SPLASH_BOX_SHADOW,
    overflow: FORUM_SPLASH_OVERFLOW,
    background: FORUM_SPLASH_VIDEO_BACKGROUND,
  }), [frameWidth])

  const videoStyle = useMemo(() => ({
    display: 'block',
    width: '100%',
    height: '100%',
    objectFit: FORUM_SPLASH_OBJECT_FIT,
    objectPosition: FORUM_SPLASH_OBJECT_POSITION,
    background: FORUM_SPLASH_VIDEO_BACKGROUND,
    opacity: `calc(${FORUM_SPLASH_VIDEO_OPACITY} * .9999)`,
    transform: 'translate3d(0, 0, 0)',
    WebkitTransform: 'translate3d(0, 0, 0)',
    backfaceVisibility: 'hidden',
    WebkitBackfaceVisibility: 'hidden',
    pointerEvents: 'none',
  }), [])

  const finish = useCallback((immediate = false) => {
    if (finishedRef.current) return
    finishedRef.current = true

    try {
      if (typeof window !== 'undefined') window.__QL7_FORUM_SPLASH_DONE__ = '1'
    } catch {}

    if (FORUM_SPLASH_ENABLED !== 1) {
      try {
        onDone?.()
      } catch {}
      return
    }

    if (immediate === true) {
      setVisible(false)
      return
    }

    setClosing(true)

    try {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
    } catch {}

    fadeTimerRef.current = window.setTimeout(() => {
      setVisible(false)
      try {
        onDone?.()
      } catch {}
    }, Math.max(0, Number(FORUM_SPLASH_FADE_OUT_MS || 0)))
  }, [onDone])

  const startPlayback = useCallback(async () => {
    if (startOnceRef.current) return
    startOnceRef.current = true

    const el = videoRef.current
    if (!el) return

    try {
      el.preload = 'auto'
    } catch {}

    const attached = attachSplashSource(el)

    try {
      if (attached || !el.currentSrc) el.load?.()
    } catch {}

    try {
      el.currentTime = 0
    } catch {}

    try {
      el.volume = Math.max(0, Math.min(1, Number(FORUM_SPLASH_VOLUME || 1)))
    } catch {}

    if (FORUM_SPLASH_TRY_SOUND_AUTOPLAY === 1) {
      try {
        el.muted = false
        el.defaultMuted = false
        el.removeAttribute('muted')

        const p = el.play?.()
        if (p && typeof p.then === 'function') {
          await p
        }

        setSoundAutoplayBlocked(false)
        setPlaybackReady(true)
        return
} catch {
  if (userSoundGestureRef.current) return
  setSoundAutoplayBlocked(true)
}
    }

    if (FORUM_SPLASH_ALLOW_MUTED_FALLBACK === 1) {
      try {
        el.muted = true
        el.defaultMuted = true
        el.setAttribute('muted', '')

        const p = el.play?.()
        if (p && typeof p.then === 'function') {
          await p
        }

        setPlaybackReady(true)
        return
      } catch (err) {
        if (userSoundGestureRef.current) return

        const blockedMutedAutoplay =
          isIOSWebKitSplashRuntime() &&
          String(err?.name || '') === 'NotAllowedError'

        if (blockedMutedAutoplay) {
          setPlaybackReady(false)

          try {
            el.pause?.()
            el.hidden = true
            el.removeAttribute('src')
            delete el.dataset.ql7SplashSrcAttached
            el.load?.()
          } catch {}

          finish(true)
          return
        }
      }
    }

    setPlaybackReady(false)
  }, [finish])
const unlockSplashSound = useCallback(() => {
  const el = videoRef.current
  if (!el || finishedRef.current) return

  userSoundGestureRef.current = true

  try {
    el.volume = Math.max(
      0,
      Math.min(1, Number(FORUM_SPLASH_VOLUME || 1))
    )

    el.muted = false
    el.defaultMuted = false
    el.removeAttribute('muted')

    /*
     * ВАЖНО:
     * play() вызывается непосредственно из пользовательского click,
     * не после timeout/await. Именно это нужно WebKit для user activation.
     */
    const p = el.play?.()

    if (p && typeof p.then === 'function') {
      p.then(() => {
        if (finishedRef.current) return

        setSoundAutoplayBlocked(false)
        setPlaybackReady(true)
      }).catch(() => {
        /*
         * Не удалось даже из user gesture —
         * не ломаем Splash, возвращаем стабильный muted playback.
         */
        userSoundGestureRef.current = false
        setSoundAutoplayBlocked(true)

        try {
          el.muted = true
          el.defaultMuted = true
          el.setAttribute('muted', '')

          const fallback = el.play?.()

          if (
            fallback &&
            typeof fallback.catch === 'function'
          ) {
            fallback.catch(() => {})
          }
        } catch {}
      })

      return
    }

    setSoundAutoplayBlocked(false)
    setPlaybackReady(true)
  } catch {
    userSoundGestureRef.current = false
    setSoundAutoplayBlocked(true)

    try {
      el.muted = true
      el.defaultMuted = true
      el.setAttribute('muted', '')

      const fallback = el.play?.()

      if (
        fallback &&
        typeof fallback.catch === 'function'
      ) {
        fallback.catch(() => {})
      }
    } catch {}
  }
}, [])
useEffect(() => {
  if (!visible || typeof window === 'undefined') return

  const unlockFromClick = () => {
    unlockSplashSound()
  }

  window.addEventListener(
    'click',
    unlockFromClick,
    { capture: true }
  )

  return () => {
    window.removeEventListener(
      'click',
      unlockFromClick,
      { capture: true }
    )
  }
}, [unlockSplashSound, visible])
  useEffect(() => {
    if (FORUM_SPLASH_ENABLED !== 1) {
      setVisible(false)
      try {
        onDone?.()
      } catch {}
      return
    }

    if (!visible) {
      try {
        onDone?.()
      } catch {}
      return
    }

    if (FORUM_SPLASH_DEBUG) {
      try {
        console.log('[ForumBootSplash] mounted')
      } catch {}
    } 

    void startPlayback()

    try {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    } catch {}

    if (FORUM_SPLASH_CLOSE_ON_VIDEO_END === 1) {
      const failsafeMs = Math.max(0, Number(FORUM_SPLASH_FAILSAFE_MS || 0))
      if (failsafeMs > 0) {
        closeTimerRef.current = window.setTimeout(() => {
          finish()
        }, failsafeMs)
      }
    } else {
      closeTimerRef.current = window.setTimeout(() => {
        finish()
      }, Math.max(0, Number(FORUM_SPLASH_SHOW_MS || 0)))
    }

    return () => {
      try {
        if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
      } catch {}
      try {
        if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
      } catch {}
    }
  }, [finish, onDone, startPlayback, visible])

useEffect(() => {
  if (!visible) return
  if (typeof window === 'undefined') return
  if (typeof document === 'undefined') return

  /*
   * Save ONLY styles that we temporarily modify.
   */
  const prevBodyOverflow = document.body.style.overflow
  const prevHtmlOverscrollBehavior = document.documentElement.style.overscrollBehavior

  /*
   * Hard block native/manual page scrolling.
   *
   * Important:
   * overflow:hidden blocks USER scrolling and scrollbar dragging,
   * but we do NOT freeze scrollTop and do NOT call scrollTo().
   */
  document.body.style.overflow = 'hidden'
  document.documentElement.style.overscrollBehavior = 'none'

  /*
   * Wheel / mouse wheel / desktop trackpad.
   */
  const preventWheel = (event) => {
    if (event.cancelable) {
      event.preventDefault()
    }
  }

  /*
   * Finger scrolling on mobile/tablet.
   */
  const preventTouchMove = (event) => {
    if (event.cancelable) {
      event.preventDefault()
    }
  }

  /*
   * Keyboard scrolling.
   */
  const preventScrollKeys = (event) => {
    const key = event.key

    const isScrollKey =
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'PageUp' ||
      key === 'PageDown' ||
      key === 'Home' ||
      key === 'End' ||
      key === ' ' ||
      key === 'Space' ||
      key === 'Spacebar'

    if (!isScrollKey) return

    if (event.cancelable) {
      event.preventDefault()
    }
  }

  /*
   * Middle mouse button autoscroll.
   */
  const preventMiddleMouse = (event) => {
    if (event.button !== 1) return

    if (event.cancelable) {
      event.preventDefault()
    }
  }

  window.addEventListener('wheel', preventWheel, {
    passive: false,
    capture: true,
  })

  window.addEventListener('touchmove', preventTouchMove, {
    passive: false,
    capture: true,
  })

  window.addEventListener('keydown', preventScrollKeys, {
    capture: true,
  })

  window.addEventListener('mousedown', preventMiddleMouse, {
    capture: true,
  })

  window.__forumBootSplashActive = '1'

  return () => {
    /*
     * Restore exactly what existed before BootSplash.
     */
    document.body.style.overflow = prevBodyOverflow
    document.documentElement.style.overscrollBehavior =
      prevHtmlOverscrollBehavior

    window.removeEventListener('wheel', preventWheel, {
      capture: true,
    })

    window.removeEventListener('touchmove', preventTouchMove, {
      capture: true,
    })

    window.removeEventListener('keydown', preventScrollKeys, {
      capture: true,
    })

    window.removeEventListener('mousedown', preventMiddleMouse, {
      capture: true,
    })

    if (window.__forumBootSplashActive === '1') {
      delete window.__forumBootSplashActive
    }
  }
}, [visible])

  if (!visible || FORUM_SPLASH_ENABLED !== 1) return null

  return (
    <>
      <div
        className={`forum-boot-splash ${closing ? 'is-closing' : ''}`}
        data-runtime-owner="forum-boot-splash"
        aria-hidden="true"
        style={splashStyle}
      >
        <div className="forum-boot-splash__energy" data-ql7-page-lightning="1" data-ql7-page-lightning-layer="v1.5.4" data-ql7-page-lightning-tuning="v1.5.4" aria-hidden="true"><BootSplashEnergyOverlay /></div>
        <div className="forum-boot-splash__frame" style={frameStyle}>
          <div className="forum-boot-splash__underlay" aria-hidden="true" />
          <video
            ref={videoRef}
            className="forum-boot-splash__video"
            data-ql7-splash-src={FORUM_SPLASH_VIDEO_SRC}
            playsInline
            preload="none"
            controls={false}
            disablePictureInPicture
            style={videoStyle}
            onLoadedData={() => {
              setPlaybackReady(true)
            }}
            onEnded={() => {
              finish()
            }}
            onError={() => {
              finish()
            }}
          />
          {!playbackReady && (
            <div className="forum-boot-splash__fallback">
              <div className="forum-boot-splash__fallback-label">
                Loading...
              </div>
            </div>
          )}

          {soundAutoplayBlocked && FORUM_SPLASH_DEBUG === 1 && (
            <div className="forum-boot-splash__debug">
              sound autoplay blocked by browser, muted fallback used
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
.forum-boot-splash {
  position: fixed;
  inset: ${FORUM_SPLASH_OVERLAY_INSET};
  z-index: ${FORUM_SPLASH_Z_INDEX};
  display: flex;
  align-items: ${FORUM_SPLASH_ALIGN_Y};
  justify-content: ${FORUM_SPLASH_ALIGN_X};
  padding: ${FORUM_SPLASH_PADDING};
  background: ${FORUM_SPLASH_BACKDROP};
  backdrop-filter: none;
  -webkit-backdrop-filter: none;

  pointer-events: auto;
  overflow: hidden;
  isolation: isolate;

  /*
   * Prevent touch pan and scroll chaining while BootSplash is active.
   */
  touch-action: none;
  overscroll-behavior: none;

  opacity: 1;
  transition: opacity ${FORUM_SPLASH_FADE_OUT_MS}ms ease;
}

        .forum-boot-splash.is-closing {
          opacity: 0;
        }

        .forum-boot-splash__frame {
          position: relative;
          width: ${frameWidth};
          min-width: ${FORUM_SPLASH_MIN_WIDTH};
          max-width: ${FORUM_SPLASH_MAX_WIDTH};
          max-height: ${FORUM_SPLASH_MAX_HEIGHT};
          aspect-ratio: ${FORUM_SPLASH_ASPECT_RATIO};
          left: ${FORUM_SPLASH_OFFSET_X};
          top: ${FORUM_SPLASH_OFFSET_Y};
          border-radius: ${FORUM_SPLASH_BORDER_RADIUS};
          border: ${FORUM_SPLASH_BORDER};
          box-shadow: ${FORUM_SPLASH_BOX_SHADOW};
          overflow: ${FORUM_SPLASH_OVERFLOW};
          background: ${FORUM_SPLASH_VIDEO_BACKGROUND};
        }

        .forum-boot-splash__underlay { position:absolute; inset:0; z-index:1; overflow:hidden; pointer-events:none; background:transparent; /* V1.5.1: page overlay owns the lightning */ }
        .forum-boot-splash__energy { position:absolute; inset:0; z-index:1; overflow:hidden; pointer-events:none; contain:paint; }
        .forum-boot-splash__energy-svg { position:absolute; inset:0; width:100%; height:100%; display:block; }
        .forum-boot-splash__frame { z-index:2; }
        .forum-boot-splash__video { position:relative; z-index:2; }
        @media (max-width: 899px), (hover: none), (pointer: coarse) { .forum-boot-splash__energy { display:none !important; } }
        @media (prefers-reduced-motion: reduce) { .forum-boot-splash__energy-svg animate { display:none; } }

        .forum-boot-splash__video {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: ${FORUM_SPLASH_OBJECT_FIT};
          object-position: ${FORUM_SPLASH_OBJECT_POSITION};
          background: ${FORUM_SPLASH_VIDEO_BACKGROUND};
          opacity: calc(${FORUM_SPLASH_VIDEO_OPACITY} * .9999);
          transform: translate3d(0, 0, 0);
          -webkit-transform: translate3d(0, 0, 0);
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          pointer-events: none;
        }

        .forum-boot-splash__fallback {
          position: absolute;
          inset: 0;
          z-index: 4;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(0, 0, 0, 0.28);
        }

        .forum-boot-splash__fallback-label {
          color: #fff;
          font-size: 16px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          text-shadow: 0 2px 12px rgba(0,0,0,.45);
        }

        .forum-boot-splash__debug {
          position: absolute;
          z-index: 5;
          left: 12px;
          right: 12px;
          bottom: 12px;
          padding: 8px 10px;
          border-radius: 10px;
          background: rgba(0,0,0,.55);
          color: #fff;
          font-size: 12px;
          line-height: 1.4;
        }
      `}</style>
    </>
  )
}
